import { FC } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import { connect } from 'react-redux';
import { RootState } from '../../redux/store';
import { isAdministrator } from '../../helpers';
import { routes, subTabs, tabs } from '../../enums';
import { TopTabsProfile } from './tabs/TopTabsProfile';
import { useGetSettingsQuery } from '../../redux/services/settings';
import { Navigate, Outlet, Route, useLocation } from 'react-router-dom';
import { TUserSlice } from 'src/redux/userSlice';
import { EventLog } from '../eventLog/EventLogList';
import { AddPhone } from '../profile/AddPhone';
import { AddPhoneBySms } from '../profile/AddPhoneBySms';
import { BindKloudAccount } from '../profile/BindKloudAccount';
import { ChangePassword } from '../profile/ChangePassword';
import { ConfirmEmail } from '../profile/ConfirmEmail';
import { DeleteProfile } from '../profile/DeleteProfile';
import { EditProfile } from '../profile/EditProfile';
import { ProviderProfile } from '../profile/ProviderProfile';
import { RestoreProfile } from '../profile/RestoreProfile';
import { UserProfile } from '../profile/UserProfile';
import { ScopesList } from '../scopes/ScopesList';

interface IPrivateRouteProps {
  profile: TUserSlice['profile'];
  roleInApp: TUserSlice['roleInApp'];
  isAuthorized: TUserSlice['isAuthorized'];
}

const mapStateToProps = ({ user }: RootState) => ({
  profile: user.profile,
  roleInApp: user.roleInApp,
  isAuthorized: user.isAuthorized,
});

const PrivateRouteComponent: FC<IPrivateRouteProps> = ({ profile, roleInApp, isAuthorized }) => {
  const location = useLocation();
  const { data: generalSettings, isFetching: settingsLoading } = useGetSettingsQuery();

  const userId = profile?.id;
  const deleted = profile?.deleted;
  const passwordChangeRequired = profile?.password_change_required;

  const needsProfileFill =
    location.pathname !== '/fill-profile' &&
    location.pathname !== `/${routes.profile}/restore-profile` &&
    userId &&
    passwordChangeRequired;

  const needsRestore =
    location.pathname !== `/${routes.profile}/restore-profile` && userId && deleted;

  if (settingsLoading || isAuthorized === null) return <LinearProgress />;
  if (!isAuthorized) return <Navigate to="/login" replace />;
  if (needsRestore) return <Navigate to="restore-profile" replace />;
  if (needsProfileFill) return <Navigate to="/fill-profile" replace />;

  if (generalSettings?.authorize_only_admins && !isAdministrator(roleInApp)) {
    return (
      <Navigate
        to="error/Вход в личный кабинет разрешён только пользователям с ролями «владелец» и «администратор»."
        replace
      />
    );
  }

  return (
    <>
      <TopTabsProfile />
      <Outlet />
    </>
  );
};

export const PrivateRoute = connect(mapStateToProps)(PrivateRouteComponent);

export const getPrivateRoutes = () => [
  <Route
    key="index"
    index
    element={<Navigate to={`/${routes.profile}/${tabs.profile}`} replace />}
  />,

  <Route key="profile" path={`/${routes.profile}/${tabs.profile}`} element={<UserProfile />} />,
  <Route
    key="edit"
    path={`/${routes.profile}/${tabs.profile}/${subTabs.edit}`}
    element={<EditProfile />}
  />,
  <Route
    key="delete"
    path={`/${routes.profile}/${tabs.profile}/${subTabs.delete}`}
    element={<DeleteProfile />}
  />,

  <Route
    key="profile-action-email"
    path={`/${routes.profile}/email/:action`}
    element={<ConfirmEmail />}
  />,
  <Route
    key="profile-action-phone"
    path={`/${routes.profile}/phone/:action`}
    element={<AddPhone />}
  />,
  <Route
    key="profile-change-password"
    path={`/${routes.profile}/change-password`}
    element={<ChangePassword />}
  />,
  <Route
    key="profile-external-provider"
    path={`/${routes.profile}/external-provider`}
    element={<ProviderProfile />}
  />,
  <Route
    key="profile-restore"
    path={`/${routes.profile}/restore-profile`}
    element={<RestoreProfile />}
  />,

  <Route
    key="profile-scopes"
    path={`/${routes.profile}/${tabs.scopes}`}
    element={<ScopesList />}
  />,
  <Route
    key="profile-event-log"
    path={`/${routes.profile}/${tabs.eventLog}`}
    element={<EventLog />}
  />,

  <Route
    key="profile-sms-add"
    path={`/${routes.profile}/sms/add/:provider_id`}
    element={<AddPhoneBySms />}
  />,
  <Route
    key="profile-kloud-add"
    path={`/${routes.profile}/kloud/add/:provider_id`}
    element={<BindKloudAccount />}
  />,
];
